'use client'

import { getConsultations } from '@/lib/api/consultations/consultation-api'
import { getExams } from '@/lib/api/exams/exam-api'
import { getOrders } from '@/lib/api/orders/order-api'
import {
    BILLING_STATUS_BADGE,
    BILLING_STATUS_LABELS,
} from '@/lib/billing/payment-labels'
import { useAuth } from '@/store/hooks'
import { format } from 'date-fns'
import { ReactNode, useEffect, useMemo, useState } from 'react'
import {
    MdAccountBalanceWallet,
    MdArrowForward,
    MdBiotech,
    MdMedicalServices,
    MdVaccines,
} from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

/**
 * Lista os atendimentos do cliente logado.
 *
 * Sem endpoint próprio: `GET /consultations|exams|orders` já filtram pelo
 * cliente do token (`getClientScope` no backend), então basta chamar os três e
 * juntar.
 */

type Kind = 'CONSULTATION' | 'EXAM' | 'PROCEDURE'

const KIND_LABELS: Record<Kind, string> = {
    CONSULTATION: 'Consulta',
    EXAM: 'Exame',
    PROCEDURE: 'Procedimento',
}

const KIND_ICONS: Record<Kind, ReactNode> = {
    CONSULTATION: <MdMedicalServices className="text-xl" />,
    EXAM: <MdBiotech className="text-xl" />,
    PROCEDURE: <MdVaccines className="text-xl" />,
}

interface Row {
    kind: Kind
    id: number
    title: string
    date: string | null
    amount: number
    billingStatus: string | null
    /** Procedimento em pagamento flex: o cliente escolhe quanto e quando pagar. */
    flexPayment: boolean
}

const formatCents = (cents: number): string =>
    (cents / 100).toLocaleString('pt-BR', {
        style: 'currency',
        currency: 'BRL',
    })

function toRow(kind: Kind, item: any): Row {
    return {
        kind,
        id: item.id,
        title: item.title || `${KIND_LABELS[kind]} #${item.id}`,
        date: item.scheduledAt || item.requestedAt || item.createdAt || null,
        amount: item.billing?.amount ?? item.finalPrice ?? 0,
        billingStatus: item.billing?.status ?? null,
        flexPayment: !!item.flexPayment,
    }
}

export default function ClientAppointmentsPage() {
    const navigate = useNavigate()
    const { user } = useAuth()

    const [loading, setLoading] = useState(true)
    const [rows, setRows] = useState<Row[]>([])

    useEffect(() => {
        let cancelled = false

        const load = async () => {
            setLoading(true)
            try {
                const [consultations, exams, orders] = await Promise.all([
                    getConsultations(),
                    getExams(),
                    getOrders(),
                ])
                if (cancelled) return

                const merged: Row[] = [
                    ...(consultations.success
                        ? (consultations.result || []).map(c =>
                              toRow('CONSULTATION', c)
                          )
                        : []),
                    ...(exams.success
                        ? (exams.result || []).map(e => toRow('EXAM', e))
                        : []),
                    ...(orders.success
                        ? (orders.result || []).map(p =>
                              toRow('PROCEDURE', p)
                          )
                        : []),
                ]

                merged.sort((a, b) => {
                    const da = a.date ? new Date(a.date).getTime() : 0
                    const db = b.date ? new Date(b.date).getTime() : 0
                    return db - da
                })

                setRows(merged)
            } catch {
                if (!cancelled)
                    toast.error('Erro ao carregar seus atendimentos')
            } finally {
                if (!cancelled) setLoading(false)
            }
        }

        load()
        return () => {
            cancelled = true
        }
    }, [])

    /** Em aberto, e o cliente pode agir: destaque no topo. */
    const flexPending = useMemo(
        () => rows.filter(r => r.flexPayment && r.billingStatus !== 'PAID'),
        [rows]
    )

    const firstName = (user?.name || '').split(' ')[0]

    return (
        <div className="flex flex-col gap-5">
            <div>
                <h1 className="text-[26px] font-bold text-base-content tracking-tight">
                    Olá, {firstName || 'paciente'} 👋
                </h1>
                <p className="text-base-content/50 mt-1">
                    Acompanhe seus atendimentos e faça seus pagamentos.
                </p>
            </div>

            {flexPending.length > 0 && (
                <div className="rounded-2xl brand-surface text-primary-content p-5 elevation-2 relative overflow-hidden">
                    <div className="pointer-events-none absolute -top-8 -right-8 w-40 h-40 rounded-full bg-white/15 blur-2xl" />
                    <div className="relative flex items-start gap-3">
                        <div className="w-11 h-11 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
                            <MdAccountBalanceWallet className="text-2xl" />
                        </div>
                        <div>
                            <p className="font-semibold text-[15px]">
                                {flexPending.length === 1
                                    ? 'Você tem um procedimento'
                                    : `Você tem ${flexPending.length} procedimentos`}{' '}
                                em pagamento Flex
                            </p>
                            <p className="text-sm text-primary-content/80 mt-0.5">
                                Você escolhe quanto pagar de cada vez e para
                                quando vence. Abra o procedimento para pagar.
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16">
                    <span className="loading loading-spinner loading-lg text-secondary" />
                </div>
            ) : rows.length === 0 ? (
                <div className="bg-base-100 rounded-2xl border border-base-200/70 elevation-1 p-12 text-center">
                    <div className="w-16 h-16 rounded-2xl bg-secondary/10 flex items-center justify-center mx-auto mb-4">
                        <MdMedicalServices className="text-3xl text-secondary/60" />
                    </div>
                    <p className="text-base-content/60">
                        Você ainda não tem atendimentos registrados.
                    </p>
                </div>
            ) : (
                <div className="flex flex-col gap-3">
                    {rows.map(r => {
                        const clickable = r.kind === 'PROCEDURE'
                        return (
                            <button
                                key={`${r.kind}-${r.id}`}
                                type="button"
                                onClick={() => navigate(`/client/${r.id}`)}
                                disabled={!clickable}
                                className={`group bg-base-100 rounded-2xl border border-base-200/70 elevation-1 p-4 text-left flex items-center gap-4 transition-all ${
                                    clickable
                                        ? 'hover:border-secondary/30 hover:-translate-y-0.5 hover:elevation-2 cursor-pointer'
                                        : 'cursor-default opacity-90'
                                }`}
                            >
                                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-secondary/15 to-secondary/5 text-secondary flex items-center justify-center shrink-0">
                                    {KIND_ICONS[r.kind]}
                                </div>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-1.5 flex-wrap">
                                        <span className="text-xs font-medium text-base-content/50">
                                            {KIND_LABELS[r.kind]}
                                        </span>
                                        {r.flexPayment && (
                                            <span className="inline-flex items-center gap-1 text-[11px] font-semibold text-secondary bg-secondary/10 rounded-full px-2 py-0.5">
                                                <MdAccountBalanceWallet className="text-xs" />
                                                Flex
                                            </span>
                                        )}
                                        {r.billingStatus && (
                                            <span
                                                className={`badge badge-sm ${
                                                    BILLING_STATUS_BADGE[
                                                        r.billingStatus
                                                    ] || 'badge-ghost'
                                                }`}
                                            >
                                                {BILLING_STATUS_LABELS[
                                                    r.billingStatus
                                                ] || r.billingStatus}
                                            </span>
                                        )}
                                    </div>
                                    <p className="font-semibold text-base-content truncate mt-0.5">
                                        {r.title}
                                    </p>
                                    <p className="text-xs text-base-content/50 mt-0.5">
                                        {r.date
                                            ? format(
                                                  new Date(r.date),
                                                  'dd/MM/yyyy'
                                              )
                                            : 'Sem data definida'}
                                        {r.amount > 0 &&
                                            ` · ${formatCents(r.amount)}`}
                                    </p>
                                </div>

                                {clickable && (
                                    <MdArrowForward className="text-xl text-base-content/25 group-hover:text-secondary group-hover:translate-x-0.5 transition-all shrink-0" />
                                )}
                            </button>
                        )
                    })}
                </div>
            )}
        </div>
    )
}
